import { useState, useRef } from 'react';
import {
    getFirestore,
    collection,
    addDoc,
    serverTimestamp
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';

interface AddApplicationModalProps {
    userId: string;
    onClose: () => void;
    onCreated: () => void;
}

export default function AddApplicationModal({
    userId,
    onClose,
    onCreated
}: AddApplicationModalProps) {
    const [company, setCompany] = useState('');
    const [role, setRole] = useState('');
    const [location, setLocation] = useState('');
    const [jobUrl, setJobUrl] = useState('');
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resumeDragActive, setResumeDragActive] = useState(false);
    const [coverDragActive, setCoverDragActive] = useState(false);
    const resumeInputRef = useRef<HTMLInputElement>(null);
    const coverInputRef = useRef<HTMLInputElement>(null);

    const handleSubmit = async () => {
        setError(null);

        if (!company.trim() || !role.trim()) {
            setError('Company name and role are required.');
            return;
        }

        // Validate URL if provided
        if (jobUrl.trim()) {
            try {
                new URL(jobUrl);
            } catch {
                setError('Please enter a valid URL (e.g., https://example.com/job).');
                return;
            }
        }

        setLoading(true);

        try {
            const db = getFirestore();
            const storage = getStorage();

            let resumePath: string | null = null;
            let coverLetterPath: string | null = null;

            // Upload resume
            if (resumeFile) {
                resumePath = `users/${userId}/resumes/${Date.now()}-${resumeFile.name}`;
                const resumeRef = ref(storage, resumePath);
                await uploadBytes(resumeRef, resumeFile);
            }

            // Upload cover letter
            if (coverLetterFile) {
                coverLetterPath = `users/${userId}/cover-letters/${Date.now()}-${coverLetterFile.name}`;
                const coverRef = ref(storage, coverLetterPath);
                await uploadBytes(coverRef, coverLetterFile);
            }

            // Create Firestore document
            await addDoc(collection(db, 'applications'), {
                userId,
                company,
                jobTitle: role,
                location,
                jobUrl: jobUrl.trim() || null,
                status: 'applied',
                source: 'manual',
                appliedDate: serverTimestamp(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                resumePath: resumePath || null,
                coverLetterPath: coverLetterPath || null,
                statusHistory: [
                    {
                        status: 'applied',
                        timestamp: new Date()
                    }
                ]
            });

            // Notify parent and close modal
            onCreated?.();
            onClose?.();
        } catch (err: any) {
            console.error('Failed to add application:', err);
            const errorMessage = err?.code === 'permission-denied'
                ? 'You do not have permission to add applications.'
                : err?.code === 'unavailable'
                ? 'Unable to connect to the server. Please check your internet connection.'
                : 'Failed to add application. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Drag and drop handlers
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleResumeDragIn = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setResumeDragActive(true);
    };

    const handleResumeDragOut = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setResumeDragActive(false);
    };

    const handleResumeDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setResumeDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            setResumeFile(files[0]);
        }
    };

    const handleCoverDragIn = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCoverDragActive(true);
    };

    const handleCoverDragOut = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCoverDragActive(false);
    };

    const handleCoverDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCoverDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            setCoverLetterFile(files[0]);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-lg shadow-xl p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Add Application
                </h3>

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">
                        {error}
                    </div>
                )}

                <div className="space-y-4">
                    <input
                        type="text"
                        placeholder="Company name *"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />

                    <input
                        type="text"
                        placeholder="Role *"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />

                    <input
                        type="text"
                        placeholder="Location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />

                    <input
                        type="url"
                        placeholder="Job posting URL (optional)"
                        value={jobUrl}
                        onChange={(e) => setJobUrl(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Resume
                        </label>
                        <div
                            onDragEnter={handleResumeDragIn}
                            onDragLeave={handleResumeDragOut}
                            onDragOver={handleDrag}
                            onDrop={handleResumeDrop}
                            onClick={() => resumeInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                resumeDragActive
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                        >
                            <input
                                ref={resumeInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                                className="hidden"
                            />
                            {resumeFile ? (
                                <div className="text-sm">
                                    <p className="text-green-600 dark:text-green-400 font-medium">✓ {resumeFile.name}</p>
                                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                                        {(resumeFile.size / 1024).toFixed(1)} KB
                                    </p>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <p className="font-medium">Drop resume here or click to upload</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">PDF, DOC, DOCX (max 10MB)</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Cover Letter
                        </label>
                        <div
                            onDragEnter={handleCoverDragIn}
                            onDragLeave={handleCoverDragOut}
                            onDragOver={handleDrag}
                            onDrop={handleCoverDrop}
                            onClick={() => coverInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                coverDragActive
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                        >
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => setCoverLetterFile(e.target.files?.[0] || null)}
                                className="hidden"
                            />
                            {coverLetterFile ? (
                                <div className="text-sm">
                                    <p className="text-green-600 dark:text-green-400 font-medium">✓ {coverLetterFile.name}</p>
                                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                                        {(coverLetterFile.size / 1024).toFixed(1)} KB
                                    </p>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <p className="font-medium">Drop cover letter here or click to upload</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">PDF, DOC, DOCX (max 10MB)</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                        {loading ? 'Saving…' : 'Add'}
                    </button>
                </div>
            </div>
        </div>
    );
}
